import React from 'react';
import { useAuth } from '../services/AuthService';
import { Container, Box, CircularProgress, Alert, AlertTitle } from '@mui/material';
import CandidateDashboard from './CandidateDashboard';
import CompanyDashboard from './CompanyDashboard';
import useUserType from '../hooks/useUserType';
import { useNavigate, useLocation } from 'react-router-dom';

const Dashboard = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    userType, 
    isCompany, 
    isJobSeeker, 
    loading: isLoading, 
    error, 
    USER_TYPES, 
    userTypeData 
  } = useUserType();

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/');
      return;
    }

    // Redirect to complete profile if user type is unknown
    if (!isLoading && userType === USER_TYPES.UNKNOWN) {
      const returnTo = `${location.pathname}${location.search || ''}`;
      navigate(`/onboarding?returnTo=${encodeURIComponent(returnTo)}`);
    }
  }, [isAuthenticated, user, navigate, isLoading, userType, USER_TYPES.UNKNOWN, location.pathname, location.search]);

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </Container>
    );
  }

  // Render the appropriate dashboard based on user type
  if (isCompany) {
    return <CompanyDashboard />;
  }

  if (isJobSeeker) {
    return <CandidateDashboard jobSeekerId={userTypeData?.profiles?.jobSeeker?.id} accountStatus={userTypeData?.profiles?.jobSeeker?.accountStatus} skillsVerified={userTypeData?.profiles?.jobSeeker?.skillsVerified} />;
  }

  // This shouldn't happen, but just in case
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Alert severity="warning">
        <AlertTitle>Profile Incomplete</AlertTitle>
        Unable to determine user type. Please complete your profile.
      </Alert>
    </Container>
  );
};

export default Dashboard;